/**
 * 가견적 편집기. **칸이 셋뿐이다** — 제목 · 면적 · 평당단가.
 *
 * 상세견적 편집기를 재사용하지 않는다. 그쪽은 공종·항목·단가표 자동완성·자동저장·
 * 충돌 감지를 들고 있는데, 세 칸짜리 화면에 그 무게를 얹으면 「대략 얼마인가」 하나를
 * 답하는 데 상세견적과 같은 품이 든다. 가견적을 최소로 두기로 한 이유가 그것이다
 * (제품 원칙(옛 5절 · 원칙 10)).
 *
 * 금액 칸은 없다. 두 칸을 곱한 값이 곧 총액이고 사람이 고칠 자리가 아니다
 * (제품 원칙(옛 원칙 2) 「총액은 항목 합계에서만 나온다」).
 */

import { useRouter } from "@/lib/nav";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import Field from "@/components/field";
import Banner from "@buildpy/ui/banner";
import SectionCard from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";
import { Input } from "@buildpy/ui/input";
import { Textarea } from "@buildpy/ui/textarea";
import { saveRoughQuote } from "@/api/actions/quote";
import { roughAmounts } from "@buildpy/core/modules/quote/rough-amount";
import {
  openSavedChannel,
  postSaved,
  savedSignalActionOf,
  ROUGH_STALE_NOTE,
  ROUGH_STALE_TOAST,
  roughConflictNoteOf,
  signalNoteOf,
  strongerSignalOf,
  type SavedChannel,
  type SavedKind,
} from "@buildpy/core/modules/quote/editor/saved-elsewhere";
import { ROUGH_TOTAL_LABEL, SUPPLY_LABEL, VAT_LABEL } from "@buildpy/core/modules/quote/snapshot-vat";
import { m2ToPyeong, quoteAreaM2 } from "@buildpy/core/modules/quote/area-unit";
import { QuoteDetail } from "@buildpy/core/modules/quote/types";
import NumberField from "@/modules/quote/ui/editor/number-field";
import Money from "@/components/money";
import { formatWon } from "@buildpy/core/lib/format";

interface Props {
  detail: QuoteDetail;
  customerName: string;
  customerSite: string;
}

const RoughEditor = ({ detail, customerName, customerSite }: Props) => {
  const router = useRouter();
  const { quote } = detail;

  const [title, setTitle] = useState(quote.title);
  // 면적 원본은 m²(#173) — 옛 가견적은 평×3.305785 로 보인다.
  const [areaM2, setAreaM2] = useState(quoteAreaM2(quote) ?? 0);
  const [unitPrice, setUnitPrice] = useState(quote.rough_unit_price ?? 0);
  const [notice, setNotice] = useState(quote.extra_notice);
  const [saving, setSaving] = useState(false);
  // 같은 브라우저의 다른 탭이 이 견적을 저장·발송·확정했다는 신호 — 잠김이면 저장을 막는다.
  const [elsewhere, setElsewhere] = useState<SavedKind | null>(null);
  const channelRef = useRef<SavedChannel | null>(null);
  // 저장이 rev 대조(0123)로 막힘 — 입력은 그대로 두고 띠의 「새로 불러오기」 로 서버 내용을 다시 보게 한다.
  const [stale, setStale] = useState(false);

  useEffect(() => {
    const channel = openSavedChannel();
    if (!channel) return;
    channelRef.current = channel;
    channel.onmessage = (e) => {
      const action = savedSignalActionOf(e.data, quote.id, quote.rev);
      if (action !== "none") setElsewhere((current) => strongerSignalOf(current, action));
    };
    return () => {
      channel.close();
      if (channelRef.current === channel) channelRef.current = null;
    };
  }, [quote.id, quote.rev]);

  /**
   * **저장과 같은 자로 센다.**
   *
   * 면적은 소수 둘째 자리에서 끊기는데(`roughQuoteSchema`·`saveRoughQuote`),
   * 화면만 안 끊으면 `34.567` 을 쳤을 때 화면은 51,850,500 원이고 저장값은
   * 51,855,000 원이 된다 — 담당자가 고객 앞에서 읽어 준 숫자가 견적서와 다르다.
   */
  const roundedM2 = Math.round(areaM2 * 100) / 100;
  // 평당 단가에 곱하는 평 — 저장(roughAreaPairOf)과 같은 환산.
  const roundedArea = m2ToPyeong(roundedM2) ?? 0;
  const amount = Math.round(roundedArea * unitPrice) || 0;
  const excl = quote.vat_mode === "EXCL";
  // 별도 가견적은 면적 × 단가가 공급가 — 저장과 같은 한 벌(roughAmounts)로 세액·총액을 낸다 · 상한을 넘기면 null(저장이 막는다).
  const split = (() => {
    try {
      return excl ? roughAmounts(roundedArea, unitPrice, "EXCL") : null;
    } catch {
      return null;
    }
  })();
  const lockedElsewhere = elsewhere !== null && elsewhere !== "saved";
  // 띠는 한 벌 — 잠김(다른 탭 발송·확정)이 먼저, 그다음 저장 막힘, 그다음 다른 탭 저장 신호.
  const bannerNote = stale && !lockedElsewhere ? ROUGH_STALE_NOTE : elsewhere !== null ? signalNoteOf(elsewhere) : null;
  const conflictNote = roughConflictNoteOf(stale, elsewhere);
  const canSave = !saving && !lockedElsewhere && !stale && title.trim() !== "" && roundedArea > 0 && unitPrice > 0;

  const save = async () => {
    setSaving(true);
    try {
      const result = await saveRoughQuote(quote.id, {
        title: title.trim(),
        area_m2: roundedM2,
        unit_price: unitPrice,
        conditions: quote.conditions,
        extra_notice: notice.trim(),
      }, quote.rev);
      if (!result.ok) {
        if (result.stale) {
          setStale(true);
          toast.error(ROUGH_STALE_TOAST);
          return;
        }
        toast.error(result.issues[0] ?? result.message);
        return;
      }
      // 듣고 있는 채널로 보낸다 — 새로 열어 보내면 이 탭의 채널도 받아 자기 저장을 남의 저장으로 띄운다.
      postSaved(channelRef.current, { quote_id: quote.id, rev: result.rev });
      toast.success(`저장했습니다 — ${formatWon(result.total)}`);
      router.push(`/app/quotes/${quote.id}`);
    } catch {
      toast.error("저장하지 못했습니다");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-xl flex-col gap-y-4">
      <div className="flex flex-col gap-y-1">
        <span className="text-sm text-muted-foreground">
          {quote.quote_no} · 가견적
        </span>
        <h1 className="text-xl font-semibold">{customerName} 고객</h1>
        {customerSite !== "" && (
          <span className="text-sm text-muted-foreground">{customerSite}</span>
        )}
      </div>

      <p className="sr-only" data-testid="rough-editor-conflict-live" role="status">
        {conflictNote}
      </p>
      {bannerNote !== null && (
        <div className="flex flex-wrap items-center gap-2">
          <Banner className="min-w-0 flex-1" live={false} testId="rough-editor-elsewhere" tone="warning">
            {bannerNote}
          </Banner>
          <Button size="sm" variant="outline" onClick={() => window.location.reload()}>
            새로 불러오기
          </Button>
        </div>
      )}

      <SectionCard as="div" className="flex flex-col gap-y-4">
        <Field label="견적 제목">
          <Input
            name="rough-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </Field>

        <div className="flex flex-wrap gap-4">
          <Field className="min-w-40 flex-1" label="면적(m²)">
            <NumberField
              ariaLabel="면적(m²)"
              // 84.5m² 처럼 소수가 온다. money 로 두면 소수점이 잘린다.
              kind="qty"
              value={areaM2}
              variant="outline"
              onCommit={setAreaM2}
            />
            {/* 평당 단가는 평으로 곱하므로 환산 평을 옆에 같이 적어 준다. */}
            <span className="text-xs text-muted-foreground">
              {roundedArea > 0 ? `≈${roundedArea}평` : "m² 단위로 입력하세요"}
            </span>
          </Field>

          <Field className="min-w-40 flex-1" label="평당 단가 (원)">
            <NumberField
              ariaLabel="평당 단가"
              value={unitPrice}
              variant="outline"
              onCommit={setUnitPrice}
            />
            <span className="text-xs text-muted-foreground">
              자재·노무·관리비를 모두 포함한 값
            </span>
          </Field>
        </div>

        <dl className="flex flex-col gap-y-1.5 border-t border-border pt-3 text-sm">
          {excl && (
            <>
              <div className="flex items-baseline justify-between">
                <dt className="text-muted-foreground">{SUPPLY_LABEL}</dt>
                <dd><Money value={split?.supply_amount} /></dd>
              </div>
              <div className="flex items-baseline justify-between">
                <dt className="text-muted-foreground">{VAT_LABEL}</dt>
                <dd><Money value={split?.vat_amount} /></dd>
              </div>
            </>
          )}
          <div className="flex items-baseline justify-between">
            <dt className="text-muted-foreground">{ROUGH_TOTAL_LABEL}</dt>
            <dd className="text-2xl font-semibold tabular-nums">
              <Money value={excl ? split?.total_amount : amount} />
            </dd>
          </div>
        </dl>
      </SectionCard>

      <Field label="안내 문구 (고객에게 보입니다)">
        <Textarea
          name="rough-notice"
          placeholder="예) 현장 실측 후 상세 견적에서 달라질 수 있습니다."
          rows={3}
          value={notice}
          onChange={(e) => setNotice(e.target.value)}
        />
      </Field>

      <div className="flex justify-end gap-x-2">
        <Button
          variant="outline"
          onClick={() => router.push(`/app/quotes/${quote.id}`)}>
          취소
        </Button>
        <Button disabled={!canSave} onClick={() => void save()}>
          {saving ? "저장 중…" : "저장"}
        </Button>
      </div>
    </div>
  );
};

export default RoughEditor;
